import * as XLSX from "xlsx";
import { TableColumn } from "../app.interface";

interface ExportOptions {
  data: Record<string, any>[];
  columns: TableColumn[];
  filename?: string;
  format: "csv" | "excel" | "json";
}

const getColumnValue = (row: Record<string, any>, column: TableColumn): any => {
  if (column.accessor) {
    if (typeof column.accessor === "function") {
      return column.accessor(row);
    }
    return row[column.accessor];
  }
  return row[column.field];
};

const formatValue = (value: any, column: TableColumn): string => {
  if (value === null || value === undefined) return "";

  switch (column.type) {
    case "datetime":
      if (value instanceof Date) {
        return value.toLocaleString();
      }
      return String(value);

    case "money":
      if (typeof value === "number") {
        return value.toLocaleString("en-US", {
          style: "currency",
          currency: "BHD",
        });
      }
      return String(value);

    case "status":
      return String(value);

    default:
      return String(value);
  }
};

const prepareExportData = (
  data: Record<string, any>[],
  columns: TableColumn[]
) => {
  // Create header row
  const headers = columns.map((col) => col.header);

  // Create data rows
  const rows = data.map((row) =>
    columns.map((column) => formatValue(getColumnValue(row, column), column))
  );

  return [headers, ...rows];
};

export const exportData = async ({
  data,
  columns,
  filename = "export",
  format,
}: ExportOptions): Promise<void> => {
  try {
    const exportData = prepareExportData(data, columns);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(exportData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    switch (format) {
      case "csv":
        XLSX.writeFile(wb, `${filename}.csv`);
        break;

      case "excel":
        XLSX.writeFile(wb, `${filename}.xlsx`);
        break;

      case "json":
        // For JSON, we'll create an array of objects with header keys
        const jsonData = data.map((row) => {
          const obj: Record<string, any> = {};
          columns.forEach((col) => {
            obj[col.header] = getColumnValue(row, col);
          });
          return obj;
        });

        // Create a Blob and trigger download
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        break;

      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  } catch (error) {
    console.error("Export failed:", error);
    throw error;
  }
};
